import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { fetchLeagueById, updateLeagueRequest } from '../api/myLeaguesApi';
import type { ILeagueItem } from '../types/myLeagues';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import '../CreateLeague.scss';
import '../MyLeagues.scss';
import './EditLeagueModal.scss';

function fileNameFromUrl(uri: string): string {
  const base = uri.split('?')[0] ?? uri;
  const seg = base.split('/').pop();
  return seg?.trim() ? seg : 'Current logo';
}

function applyLeagueToForm(
  league: ILeagueItem,
  setters: {
    setLeagueName: (v: string) => void;
    setOrganizerName: (v: string) => void;
    setOrganizerEmailId: (v: string) => void;
    setOrganizerPhoneNumber: (v: string) => void;
    setCity: (v: string) => void;
    setState: (v: string) => void;
    setCountry: (v: string) => void;
    setAssociation: (v: string) => void;
    setCountryCode: (v: string) => void;
    setAllowPlayersToJoin: (v: boolean) => void;
    setLeagueLogoLabel: (v: string) => void;
    setImageFile: (v: File | null) => void;
  },
) {
  const cc = String(league.countryCode ?? 91);
  const digits = (league.organizerPhoneNumber ?? '').replace(/\D/g, '');
  const national = digits.length >= 10 ? digits.slice(-10) : digits;
  setters.setLeagueName(league.leagueName ?? '');
  setters.setOrganizerName(league.organizerName ?? '');
  setters.setOrganizerEmailId(league.organizerEmailId ?? '');
  setters.setCity(league.city ?? '');
  setters.setState(league.state ?? '');
  setters.setCountry(league.country ?? '');
  setters.setAssociation(league.association ?? '');
  setters.setCountryCode(cc);
  setters.setAllowPlayersToJoin(Boolean(league.allowPlayersToJoin));
  setters.setOrganizerPhoneNumber(national ? `${cc}${national}` : '');
  setters.setLeagueLogoLabel(league.imageUri ? fileNameFromUrl(league.imageUri) : '');
  setters.setImageFile(null);
}

interface EditLeagueModalProps {
  open: boolean;
  league: ILeagueItem | null;
  onOpenChange: (open: boolean) => void;
  onUpdated?: () => void;
}

function EditLeagueModal({ open, league, onOpenChange, onUpdated }: EditLeagueModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const leagueId = league?.leagueId ?? null;

  const [leagueName, setLeagueName] = useState('');
  const [organizerName, setOrganizerName] = useState('');
  const [organizerPhoneNumber, setOrganizerPhoneNumber] = useState('');
  const [organizerEmailId, setOrganizerEmailId] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [association, setAssociation] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [allowPlayersToJoin, setAllowPlayersToJoin] = useState(false);
  const [leagueLogoLabel, setLeagueLogoLabel] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [hadImageUri, setHadImageUri] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: details } = useQuery({
    queryKey: [QUERY_KEY.getLeagueDetails, leagueId],
    queryFn: () => fetchLeagueById(leagueId!),
    enabled: open && !!leagueId,
  });

  useEffect(() => {
    if (!open || !league) return;
    applyLeagueToForm(league, {
      setLeagueName,
      setOrganizerName,
      setOrganizerEmailId,
      setOrganizerPhoneNumber,
      setCity,
      setState,
      setCountry,
      setAssociation,
      setCountryCode,
      setAllowPlayersToJoin,
      setLeagueLogoLabel,
      setImageFile,
    });
    setHadImageUri(Boolean(league.imageUri));
  }, [open, league]);

  useEffect(() => {
    if (!details || !open) return;
    const item: ILeagueItem = {
      leagueId: details.leagueId,
      leagueName: details.leagueName,
      organizerName: details.organizerName,
      organizerEmailId: details.organizerEmailId,
      organizerPhoneNumber: details.organizerPhoneNumber,
      city: details.city,
      state: details.state,
      country: details.country,
      association: details.association,
      countryCode: details.countryCode,
      allowPlayersToJoin: details.allowPlayersToJoin,
      imageUri: details.imageUri,
    };
    applyLeagueToForm(item, {
      setLeagueName,
      setOrganizerName,
      setOrganizerEmailId,
      setOrganizerPhoneNumber,
      setCity,
      setState,
      setCountry,
      setAssociation,
      setCountryCode,
      setAllowPlayersToJoin,
      setLeagueLogoLabel,
      setImageFile,
    });
    setHadImageUri(Boolean(details.imageUri));
  }, [details, open]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setLeagueLogoLabel(file.name);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setImageFile(null);
    }
    onOpenChange(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leagueId) return;

    const hasLogo = Boolean(imageFile || hadImageUri);
    if (!hasLogo) {
      toast.error('Please upload a league logo.');
      return;
    }

    setSubmitting(true);
    try {
      await updateLeagueRequest({
        leagueId,
        leagueName,
        organizerName,
        organizerPhoneNumber,
        organizerEmailId,
        city,
        state,
        country,
        countryCode: parseInt(countryCode, 10) || 91,
        association,
        allowPlayersToJoin,
        imageFile,
      });
      await queryClient.invalidateQueries({ queryKey: ['myLeagues', 'allLeague'] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getLeagueDetails, leagueId] });
      toast.success('League updated successfully.');
      handleOpenChange(false);
      onUpdated?.();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: unknown } };
      const data = ax?.response?.data;
      const message =
        typeof data === 'string'
          ? data
          : (data && typeof data === 'object' && 'message' in data
              ? String((data as { message: unknown }).message)
              : null) ?? null;
      toast.error(message || 'Failed to update league. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="create-league-shell-overlay edit-league-modal-overlay" />
        <Dialog.Content className="edit-league-modal-content" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="edit-league-modal-header">
            <Dialog.Title className="edit-league-modal-title">Edit League</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="edit-league-modal-close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>

            <form onSubmit={handleSubmit} className="create-league-step-form league-details-step edit-league-modal-form">
              <div className="league-details-grid">
                <div className="league-details-col">
                  <div className="create-league-field">
                    <label>
                      League Logo <span className="req">*</span>
                    </label>
                    <div className="league-logo-input-wrap">
                      <input
                        type="text"
                        placeholder="IMG.NMNH.jpg"
                        value={leagueLogoLabel}
                        readOnly
                        className="league-logo-text"
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="league-logo-file"
                        onChange={handleLogoChange}
                      />
                      <button
                        type="button"
                        className="league-logo-upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                          <path d="M12 4l-6 6h4v8h4v-8h4l-6-6z" fill="currentColor" />
                        </svg>
                        Upload
                      </button>
                    </div>
                  </div>
                  <div className="create-league-field">
                    <label>
                      Organiser Name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Organiser Name"
                      value={organizerName}
                      onChange={(ev) => setOrganizerName(ev.target.value)}
                      required
                    />
                  </div>
                  <div className="form-control-wrap create-league-phone-field">
                    <div className="form-group">
                      <label>
                        Mobile Number <span className="req">*</span>
                      </label>
                      <div className="phone">
                        <PhoneInput
                          country="in"
                          value={organizerPhoneNumber}
                          countryCodeEditable={false}
                          onChange={(val, c) => {
                            setOrganizerPhoneNumber(val);
                            if (c?.dialCode) setCountryCode(String(c.dialCode));
                          }}
                          inputClass="form-control phone-input-field"
                          containerClass="phone-input-container"
                          placeholder="Enter Mobile Number"
                          enableSearch
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="league-details-col">
                  <div className="create-league-field">
                    <label>
                      League Name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter League Name"
                      value={leagueName}
                      onChange={(ev) => setLeagueName(ev.target.value)}
                      required
                    />
                  </div>
                  <div className="create-league-field">
                    <label>
                      Organiser Email <span className="req">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="Enter Email Id"
                      value={organizerEmailId}
                      onChange={(ev) => setOrganizerEmailId(ev.target.value)}
                      required
                    />
                  </div>
                  <div className="create-league-field">
                    <label>
                      Association <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Association"
                      value={association}
                      onChange={(ev) => setAssociation(ev.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="league-details-address-row">
                <div className="create-league-field">
                  <label>
                    City <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter city"
                    value={city}
                    onChange={(ev) => setCity(ev.target.value)}
                    required
                  />
                </div>
                <div className="create-league-field">
                  <label>
                    State <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter State"
                    value={state}
                    onChange={(ev) => setState(ev.target.value)}
                    required
                  />
                </div>
                <div className="create-league-field">
                  <label>
                    Country <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Country"
                    value={country}
                    onChange={(ev) => setCountry(ev.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="create-league-field create-league-toggle">
                <div className="create-league-toggle-copy">
                  <label>Allow any Player to Join</label>
                  <p className="create-league-toggle-desc">When enabled, players can request to join this league.</p>
                </div>
                <button
                  type="button"
                  className={`create-league-switch ${allowPlayersToJoin ? 'on' : ''}`}
                  onClick={() => setAllowPlayersToJoin(!allowPlayersToJoin)}
                  role="switch"
                  aria-checked={allowPlayersToJoin}
                >
                  <span className="create-league-switch-slider" />
                </button>
              </div>

              <div className="create-league-footer">
                <button type="submit" className="create-league-submit" disabled={submitting}>
                  {submitting ? 'Updating…' : 'Update League'}
                </button>
              </div>
            </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default EditLeagueModal;
